import { useState, useCallback, useMemo } from 'react';
import { startOfWeek, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, startOfMonth } from 'date-fns';
import { getWeekDays } from '../components/Calendar/utils/getWeekDays';

export enum CalendarView {
  Dia = 0,
  Semana = 1,
  Mes = 2,
}

export interface ViewOption {
  label: string;
  value: CalendarView;
}

interface UseCalendarNavigationReturn {
  currentDate: Date;
  view: CalendarView;
  setView: (view: CalendarView) => void;
  viewsOptions: ViewOption[];
  handleViewChange: (value: string | number | null) => void;
  goToToday: () => void;
  navigate: (direction: 'prev' | 'next') => void;
  navigateByDay: (direction: 'prev' | 'next') => void;
  goToDate: (date: Date) => void;
  weekDays: Date[];
}

export const useCalendarNavigation = (
  initialDate?: Date,
  initialView: CalendarView = CalendarView.Semana,
): UseCalendarNavigationReturn => {
  const [currentDate, setCurrentDate] = useState<Date>(initialDate || startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [view, setView] = useState<CalendarView>(initialView);

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  const viewsOptions: ViewOption[] = useMemo(
    () => [
      { label: 'Dia', value: CalendarView.Dia },
      { label: 'Semana', value: CalendarView.Semana },
      { label: 'Mês', value: CalendarView.Mes },
    ],
    [],
  );

  const goToToday = useCallback(() => {
    if (view === CalendarView.Dia) {
      setCurrentDate(new Date());
    } else if (view === CalendarView.Mes) {
      setCurrentDate(startOfWeek(startOfMonth(new Date()), { weekStartsOn: 0 }));
    } else {
      setCurrentDate(startOfWeek(new Date(), { weekStartsOn: 0 }));
    }
  }, [view]);

  const navigate = useCallback(
    (direction: 'prev' | 'next') => {
      setCurrentDate(prev => {
        if (view === CalendarView.Semana) {
          const newDate = direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1);
          return startOfWeek(newDate, { weekStartsOn: 0 });
        } else if (view === CalendarView.Dia) {
          return direction === 'next' ? addDays(prev, 1) : subDays(prev, 1);
        } else {
          // CalendarView.Mes — prev é o gridStart (domingo); addDays(6) cai no mês alvo
          const targetMonthDay = addDays(prev, 6);
          const nextMonth = direction === 'next' ? addMonths(targetMonthDay, 1) : subMonths(targetMonthDay, 1);
          return startOfWeek(startOfMonth(nextMonth), { weekStartsOn: 0 });
        }
      });
    },
    [view],
  );

  const navigateByDay = useCallback((direction: 'prev' | 'next') => {
    setCurrentDate(prev => (direction === 'next' ? addDays(prev, 1) : subDays(prev, 1)));
  }, []);

  const goToDate = useCallback((date: Date) => {
    setCurrentDate(startOfWeek(date, { weekStartsOn: 0 }));
  }, []);

  const handleViewChange = useCallback((value: string | number | null) => {
    if (value !== null && Object.values(CalendarView).includes(value as CalendarView)) {
      if (value === CalendarView.Dia) {
        setCurrentDate(new Date());
      } else if (value === CalendarView.Mes) {
        setCurrentDate(startOfWeek(startOfMonth(new Date()), { weekStartsOn: 0 }));
      } else {
        setCurrentDate(startOfWeek(new Date(), { weekStartsOn: 0 }));
      }
      setView(value as CalendarView);
    }
  }, []);

  return {
    currentDate,
    view,
    setView,
    viewsOptions,
    handleViewChange,
    goToToday,
    navigate,
    navigateByDay,
    goToDate,
    weekDays,
  };
};
